// A plan sheet you can actually zoom into (Ryan, Sep 30 2026: "the zoom does not work
// that well"). Before this, zoom was a width toggle — 2× meant "twice the box wide,
// now scroll around" — and the full viewer's 1–4× steps always grew from the top-left.
//
// Here the picture sits in a pane and:
//   · the wheel (or a pinch) zooms around the point under the cursor,
//   · dragging pans, double-click zooms in on that spot, + / − / Fit buttons,
//   · keyboard + − 0 work while the pane is hovered,
//   · pins are drawn by the caller inside the same frame in % of the sheet, and the
//     caller gets the current scale so a pin can counter-scale and stay the same size.
//
// overlay(scale) → the pins; they are positioned in % inside the sheet frame.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const MIN = 1, MAX = 16;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

export default function SheetZoomPane({ src, alt = '', width = 0, height = 0, overlay = null, background = '#e5e7eb',
  toolbar = true, dark = false, empty = 'No image for this sheet.', style = {}, sheetKey, note = null }) {
  const boxRef = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [nat, setNat] = useState({ w: width || 0, h: height || 0 });   // the picture's own size
  // The view: scale × the fit size, then pushed by (tx, ty) px inside the box.
  const [view, setView] = useState({ s: 1, tx: 0, ty: 0 });
  const drag = useRef(null);           // { x, y, tx, ty, moved }
  const pointers = useRef(new Map());  // active pointers, for pinch
  const pinch = useRef(null);          // { d, s, cx, cy }

  useLayoutEffect(() => {
    const el = boxRef.current; if (!el) return;
    const read = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    read();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(read) : null;
    ro && ro.observe(el);
    return () => ro && ro.disconnect();
  }, []);
  useEffect(() => { if (width && height) setNat({ w: width, h: height }); }, [width, height]);
  // A new sheet starts fitted. Keyed on sheetKey when given, so the sharp copy of the SAME
  // sheet arriving (it swaps `src`) doesn't throw away where you have zoomed to.
  const resetOn = sheetKey !== undefined ? sheetKey : src;
  useEffect(() => { setView({ s: 1, tx: 0, ty: 0 }); }, [resetOn]);
  // While zooming/moving the frame is a GPU layer (smooth); once it has been still a
  // moment the layer goes, so the browser redraws the drawing sharp at the new size —
  // a will-change layer is otherwise kept at its first, fitted resolution and blown up.
  const [moving, setMoving] = useState(false);
  const stillTimer = useRef(null);
  useEffect(() => {
    setMoving(true);
    clearTimeout(stillTimer.current);
    stillTimer.current = setTimeout(() => setMoving(false), 250);
    return () => clearTimeout(stillTimer.current);
  }, [view.s, view.tx, view.ty]);

  // The fit size: the whole sheet inside the box, its shape kept.
  const a = nat.w && nat.h ? nat.w / nat.h : 4 / 3;
  const fitW = box.w && box.h ? Math.min(box.w, box.h * a) : 0;
  const fitH = a ? fitW / a : 0;

  // Keep the sheet inside the box: centred while it is smaller than the box on an
  // axis, and never dragged past its own edge once it is bigger.
  const settle = useCallback((v) => {
    const cw = fitW * v.s, ch = fitH * v.s;
    const tx = cw <= box.w ? (box.w - cw) / 2 : clamp(v.tx, box.w - cw, 0);
    const ty = ch <= box.h ? (box.h - ch) / 2 : clamp(v.ty, box.h - ch, 0);
    return { s: v.s, tx, ty };
  }, [fitW, fitH, box.w, box.h]);

  // Zoom to `s2` keeping the sheet point under (px, py) — box coordinates — still.
  const zoomAt = useCallback((s2, px, py) => setView(v => {
    const s = clamp(s2, MIN, MAX);
    const k = s / v.s;
    return settle({ s, tx: px - (px - v.tx) * k, ty: py - (py - v.ty) * k });
  }), [settle]);
  const zoomBy = useCallback((f, px, py) => setView(v => {
    const s = clamp(v.s * f, MIN, MAX);
    const k = s / v.s;
    const cx = px ?? box.w / 2, cy = py ?? box.h / 2;
    return settle({ s, tx: cx - (cx - v.tx) * k, ty: cy - (cy - v.ty) * k });
  }), [settle, box.w, box.h]);
  const fit = useCallback(() => setView(settle({ s: 1, tx: 0, ty: 0 })), [settle]);
  useEffect(() => { setView(v => settle(v)); }, [settle]);

  const local = e => { const r = boxRef.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };

  // The wheel zooms — React's onWheel is passive, so the page would scroll too; a real
  // listener with preventDefault keeps the wheel on the sheet.
  useEffect(() => {
    const el = boxRef.current; if (!el) return;
    const onWheel = e => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const f = Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.0018));
      zoomBy(f, e.clientX - r.left, e.clientY - r.top);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomBy]);

  function onPointerDown(e) {
    if (e.button != null && e.button !== 0) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [p1, p2] = [...pointers.current.values()];
      const r = boxRef.current.getBoundingClientRect();
      pinch.current = { d: Math.hypot(p2.x - p1.x, p2.y - p1.y), s: view.s, cx: (p1.x + p2.x) / 2 - r.left, cy: (p1.y + p2.y) / 2 - r.top };
      drag.current = null;
      return;
    }
    drag.current = { x: e.clientX, y: e.clientY, tx: view.tx, ty: view.ty, moved: false };
  }
  function onPointerMove(e) {
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch.current && pointers.current.size >= 2) {
      const [p1, p2] = [...pointers.current.values()];
      const d = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1;
      zoomAt(pinch.current.s * (d / pinch.current.d), pinch.current.cx, pinch.current.cy);
      return;
    }
    const d = drag.current; if (!d) return;
    const dx = e.clientX - d.x, dy = e.clientY - d.y;
    if (!d.moved && Math.hypot(dx, dy) < 4) return;
    // Only once it is really a drag: capturing earlier would swallow a tap on a pin.
    if (!d.moved) { d.moved = true; try { boxRef.current.setPointerCapture(e.pointerId); } catch {} }
    setView(v => settle({ s: v.s, tx: d.tx + dx, ty: d.ty + dy }));
  }
  function onPointerUp(e) {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    // A drag that moved must not count as a click on whatever is under the pointer.
    if (drag.current && drag.current.moved) {
      const stop = ev => { ev.stopPropagation(); ev.preventDefault(); };
      boxRef.current.addEventListener('click', stop, { capture: true, once: true });
      setTimeout(() => boxRef.current && boxRef.current.removeEventListener('click', stop, { capture: true }), 0);
    }
    drag.current = null;
  }
  function onDoubleClick(e) {
    if (e.target.closest('button')) return;
    const [px, py] = local(e);
    if (view.s >= MAX * 0.99) fit(); else zoomBy(2, px, py);
  }
  function onKeyDown(e) {
    if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomBy(1.5); }
    else if (e.key === '-' || e.key === '_') { e.preventDefault(); zoomBy(1 / 1.5); }
    else if (e.key === '0') { e.preventDefault(); fit(); }
  }

  const pct = Math.round(view.s * 100);
  const btn = { border: 'none', background: 'transparent', color: dark ? '#fff' : '#111827', fontSize: 13, fontWeight: 900, cursor: 'pointer', padding: '4px 9px', lineHeight: 1 };
  return (
    <div ref={boxRef} tabIndex={0} onKeyDown={onKeyDown}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
      onDoubleClick={onDoubleClick}
      style={{ position: 'relative', overflow: 'hidden', background, touchAction: 'none', userSelect: 'none', outline: 'none',
        cursor: view.s > 1 ? 'grab' : 'zoom-in', ...style }}>
      {src ? (
        <div style={{ position: 'absolute', left: 0, top: 0, width: fitW || undefined, height: fitH || undefined,
          transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.s})`, transformOrigin: '0 0', willChange: moving ? 'transform' : 'auto' }}>
          <img src={src} alt={alt} draggable={false}
            onLoad={e => { if (!width || !height) setNat({ w: e.target.naturalWidth, h: e.target.naturalHeight }); }}
            style={{ display: 'block', width: '100%', height: '100%', background: '#fff', pointerEvents: 'none' }} />
          {overlay && overlay(view.s)}
        </div>
      ) : (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', padding: 40, textAlign: 'center' }}>{empty}</div>
      )}
      {toolbar && src && (
        <div onPointerDown={e => e.stopPropagation()} onDoubleClick={e => e.stopPropagation()}
          style={{ position: 'absolute', right: 10, top: 10, display: 'inline-flex', alignItems: 'center', borderRadius: 8, overflow: 'hidden',
            background: dark ? 'rgba(17,24,39,0.85)' : 'rgba(255,255,255,0.92)', border: `1px solid ${dark ? 'rgba(255,255,255,0.3)' : '#d1d5db'}`, boxShadow: '0 2px 8px rgba(0,0,0,0.18)' }}>
          <button type="button" onClick={() => zoomBy(1 / 1.5)} title="Zoom out (−)" style={btn}>−</button>
          <span style={{ fontSize: 11.5, fontWeight: 800, color: dark ? '#fff' : '#374151', minWidth: 44, textAlign: 'center' }}>{pct}%</span>
          <button type="button" onClick={() => zoomBy(1.5)} title="Zoom in (+)" style={btn}>+</button>
          <button type="button" onClick={fit} title="Fit the whole sheet (0)" style={{ ...btn, fontSize: 11.5, borderLeft: `1px solid ${dark ? 'rgba(255,255,255,0.25)' : '#e5e7eb'}` }}>Fit</button>
        </div>
      )}
      {note && src && (
        <div style={{ position: 'absolute', right: 10, bottom: 8, fontSize: 11, fontWeight: 700, color: dark ? 'rgba(255,255,255,0.85)' : '#4b5563', background: dark ? 'rgba(17,24,39,0.7)' : 'rgba(255,255,255,0.9)', borderRadius: 6, padding: '2px 7px', pointerEvents: 'none' }}>{note}</div>
      )}
      {toolbar && src && view.s === 1 && (
        <div style={{ position: 'absolute', left: 10, bottom: 8, fontSize: 11, color: dark ? 'rgba(255,255,255,0.8)' : '#6b7280', background: dark ? 'rgba(17,24,39,0.7)' : 'rgba(255,255,255,0.85)', borderRadius: 6, padding: '2px 7px', pointerEvents: 'none' }}>
          Scroll or pinch to zoom · drag to move · double-click to zoom in
        </div>
      )}
    </div>
  );
}
